"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

import type { AppThemeTone } from "@/components/editorial/product-bridge/app-surface";

import { ReplayPlayer } from "./replay";
import type { ReplayStory } from "./replay-story";

/**
 * Holds the replay's first frame, rendered on the server, until the player
 * starts from it: when a third of the frame is in view. With reduced motion
 * the player mounts at once on its final frame instead of playing.
 *
 * The player ships with the route rather than as a lazy chunk, for the
 * reason GuidedIsland gives: an async chunk sharing the markdown and motion
 * libraries costs every page, the home included.
 */
export function ReplayIsland({
  replay,
  tone,
  children,
}: {
  replay: ReplayStory;
  tone: AppThemeTone;
  children: ReactNode;
}) {
  const islandRef = useRef<HTMLDivElement>(null);
  const [player, setPlayer] = useState<{ reduceMotion: boolean } | null>(null);

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const island = islandRef.current;
    if (reduceMotion || !island || typeof IntersectionObserver === "undefined") {
      setPlayer({ reduceMotion });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        setPlayer({ reduceMotion });
      },
      { threshold: 0.35 },
    );
    observer.observe(island);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={islandRef}>
      {player ? <ReplayPlayer replay={replay} tone={tone} reduceMotion={player.reduceMotion} /> : children}
    </div>
  );
}
